import { mdiCheckCircleOutline, mdiPuzzlePlusOutline } from "@mdi/js";
import type {
  EntitlementState,
  FeatureEntitlementItem,
  FlagValue,
} from "@/queries/modules/entitlements";

interface StateDisplay {
  label: string;
  color?: string;
}

interface AccessChip {
  label: string;
  icon: string;
  color?: string;
}

// The tier chip shown beside a feature's description, keyed off classification:
//   "Add-on"   — addon: a paid in-module upsell needing its own grant.
//   "Incluído" — core: bundled with the module, on once the module is reachable,
//                nothing to manage here (managed through the module).
// Every feature is now core or addon, so a chip is always present.
export const featureAccessChip = (feature: FeatureEntitlementItem): AccessChip => {
  if (feature.classification === "addon") {
    return { label: "Add-on", icon: mdiPuzzlePlusOutline, color: "primary" };
  }
  return { label: "Incluído", icon: mdiCheckCircleOutline };
};

const STATE_DISPLAY: Record<EntitlementState, StateDisplay> = {
  entitled: { label: "Ativo", color: "success" },
  // `teal` (registered in the Vuetify theme), not `info` — info is the same
  // #4f7cff as primary, so a trial chip on a blue Add-on row would read as the
  // tier color. Teal gives the in-trial lifecycle its own hue.
  trialing: { label: "Em trial", color: "teal" },
  // `locked` is the upsell state. The payload carries no price/SKU yet, so the
  // grant CTA stands in for the future upsell surface (open product question).
  locked: { label: "Não contratado", color: "grey" },
  disabled_by_tenant: { label: "Desativado pelo cliente", color: "warning" },
  suspended: { label: "Suspenso", color: "error" },
  expired: { label: "Trial expirado", color: "warning" },
};

export const stateDisplay = (state: EntitlementState): StateDisplay => {
  return STATE_DISPLAY[state] || { label: state, color: "grey" };
};

// Feature rows show a single status chip that folds lifecycle state and bool
// value together: a usable-but-off flag reads "Desligada", and the
// unremarkable "entitled" default stays uncolored so divergent states
// (suspended, expired, locked…) stand out.
export const featureStatusDisplay = (
  feature: FeatureEntitlementItem,
): StateDisplay => {
  const usable = feature.state === "entitled" || feature.state === "trialing";
  if (usable && !feature.value) {
    return { label: "Desligada" };
  }
  const display = stateDisplay(feature.state);
  return feature.state === "entitled" ? { label: display.label } : display;
};

export const formatFlagValue = (value: FlagValue | null | undefined): string => {
  if (value === null || value === undefined) return "—";
  if (typeof value === "number") return value.toLocaleString("pt-BR");
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
};

// Plans are code-declared on the backend and only expose their key; the human
// title is a frontend decision.
const PLAN_LABELS: Record<string, string> = {
  nfce_full: "NFC-e Completo",
};

export const planLabel = (planKey: string): string => {
  return PLAN_LABELS[planKey] || planKey;
};
